import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { Globalization } from "@awesome-cordova-plugins/globalization/ngx";

@Injectable({
  providedIn: "root",
})
export class TimezoneService {
  private appStateListener?: PluginListenerHandle;
  private initialized = false;

  private timezoneSubject = new BehaviorSubject<string>("+0000");
  private timezoneNameSubject = new BehaviorSubject<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  timezone$ = this.timezoneSubject.asObservable();
  timezoneName$ = this.timezoneNameSubject.asObservable();

  constructor(private globalization: Globalization) {}

  get timezone(): string {
    return this.timezoneSubject.value;
  }

  get timezoneName(): string {
    return this.timezoneNameSubject.value;
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    await this.refreshTimezone();

    this.appStateListener = await App.addListener("appStateChange", async (state) => {
      if (state.isActive) {
        await this.refreshTimezone();
      }
    });
  }

  async refreshTimezone(): Promise<void> {
    const jsFallbackOffset = -new Date().getTimezoneOffset();
    const intlTimezoneName =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const intlOffset = this.getOffsetMinutesFromTimezoneName(intlTimezoneName);

    if (!this.isNativePlatform()) {
      this.timezoneNameSubject.next(intlTimezoneName);
      this.timezoneSubject.next(this.toOffsetString(intlOffset ?? jsFallbackOffset));
      return;
    }

    const selectionBaseline = intlOffset ?? jsFallbackOffset;
    let minutesEastOfUtc = jsFallbackOffset;
    let timezoneName = intlTimezoneName;

    try {
      const pattern = await this.globalization.getDatePattern({
        formatLength: "short",
        selector: "date and time",
      });

      const nativeTzName = pattern?.iana_timezone || pattern?.timezone;
      if (nativeTzName) {
        timezoneName = nativeTzName;
      }

      const offsetFromIana = this.getOffsetMinutesFromTimezoneName(timezoneName);
      if (offsetFromIana !== null) {
        minutesEastOfUtc = offsetFromIana;
      } else {
        const utcOffsetRaw = Number(pattern?.utc_offset);
        const dstOffsetRaw = Number(pattern?.dst_offset);

        if (Number.isFinite(utcOffsetRaw)) {
          const utcOffsetMinutes =
            Math.abs(utcOffsetRaw) > 24 * 60
              ? Math.round(utcOffsetRaw / 60)
              : utcOffsetRaw;

          if (Number.isFinite(dstOffsetRaw)) {
            const dstOffsetMinutes =
              Math.abs(dstOffsetRaw) > 24 * 60
                ? Math.round(dstOffsetRaw / 60)
                : dstOffsetRaw;

            // Some platforms already include DST in utc_offset.
            const candidates = [
              utcOffsetMinutes,
              dstOffsetMinutes,
              utcOffsetMinutes + dstOffsetMinutes,
            ];
            minutesEastOfUtc = candidates.reduce((best, candidate) => {
              return Math.abs(candidate - selectionBaseline) <
                Math.abs(best - selectionBaseline)
                ? candidate
                : best;
            }, utcOffsetMinutes);
          } else {
            minutesEastOfUtc = utcOffsetMinutes;
          }
        }
      }
    } catch {
      // Use JS fallback when native plugin is unavailable.
      minutesEastOfUtc = jsFallbackOffset;
      timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    }

    this.timezoneNameSubject.next(timezoneName);
    this.timezoneSubject.next(this.toOffsetString(minutesEastOfUtc));
  }

  private getOffsetMinutesFromTimezoneName(timezoneName: string): number | null {
    if (!timezoneName) return null;

    try {
      const now = new Date();
      const zoned = new Date(now.toLocaleString("en-US", { timeZone: timezoneName }));
      const utc = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
      const diffMinutes = Math.round((zoned.getTime() - utc.getTime()) / 60000);

      return Number.isFinite(diffMinutes) ? diffMinutes : null;
    } catch {
      return null;
    }
  }

  private isNativePlatform(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === "ios" || platform === "android";
  }

  private toOffsetString(minutesEastOfUtc: number): string {
    const sign = minutesEastOfUtc >= 0 ? "+" : "-";
    const abs = Math.abs(minutesEastOfUtc);
    const hours = Math.floor(abs / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (abs % 60).toString().padStart(2, "0");

    return `${sign}${hours}${minutes}`;
  }
}
